import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { ScrollText, Download, Eye, RotateCcw, Loader2, ShieldCheck, Lock, Mail } from "lucide-react";
import SignaturePad from "../../components/SignaturePad";
import { api, API as API_BASE } from "../../lib/api";

const TRUSTEE_ROLES = [
  { v: "", label: "— None / general trustee —" },
  { v: "chair", label: "Chair" },
  { v: "secretary", label: "Secretary" },
  { v: "treasurer", label: "Treasurer" },
  { v: "dsl", label: "Designated Safeguarding Lead" },
  { v: "education_lead", label: "Education Lead" },
  { v: "trustee", label: "Trustee (general)" },
];

/**
 * Trustee declaration form — Charity-Commission-style:
 *   • Trustee block: name, address, email, DOB, role, signed date + signature
 *   • Chair counter-sign block: name, date + signature
 *   • Buttons: Preview PDF (opens new tab), Download signed PDF (persists + downloads)
 */
export default function TrusteeDeclarationManager() {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    trustee_name: "", trustee_address: "", trustee_email: "",
    trustee_dob: "", trustee_role: "",
    trustee_signed_on: todayISO,
    chair_name: "", chair_signed_on: todayISO,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const trusteeSig = useRef(null);
  const chairSig = useRef(null);
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/governance/trustee-declarations");
      setHistory(r.data.items || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const collect = () => {
    if (trusteeSig.current?.isEmpty()) {
      toast.error("Trustee signature is empty.");
      return null;
    }
    if (chairSig.current?.isEmpty()) {
      toast.error("Chair counter-signature is empty.");
      return null;
    }
    const required = ["trustee_name", "trustee_address", "trustee_email", "trustee_dob", "chair_name"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast.error(`Please fill in: ${k.replace("_", " ")}`);
        return null;
      }
    }
    return {
      ...form,
      trustee_role: form.trustee_role || null,
      trustee_signature_dataurl: trusteeSig.current.toDataURL(),
      chair_signature_dataurl: chairSig.current.toDataURL(),
    };
  };

  const post = async (path, asDownload) => {
    const body = collect();
    if (!body) return;
    setBusy(path);
    try {
      const res = await fetch(`${API_BASE}/admin/governance/${path}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (asDownload) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `trustee-declaration-${form.trustee_name.replace(/\W+/g, "_") || "signed"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        const emailed = res.headers.get("x-email-sent") === "1";
        toast.success(`Signed declaration saved & downloaded.${emailed ? " Copy emailed to trustee + chair." : ""}`);
        loadHistory();
      } else {
        window.open(url, "_blank");
      }
    } catch (e) {
      toast.error(e.message || "Failed to generate PDF");
    }
    setBusy(null);
  };

  return (
    <div className="space-y-6" data-testid="trustee-declaration">
      <div>
        <h3 className="font-display text-xl text-navy flex items-center gap-2">
          <ScrollText size={18} className="text-saffron" /> Trustee declaration
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Fill in below to generate a Charity-Commission-style declaration. The form supports both signatures
          (trustee + chair) in one go, so this can be used face-to-face at a trustee meeting or with the chair
          present remotely. The PDF carries a tamper-evidence fingerprint in its footer.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* TRUSTEE BLOCK */}
        <section className="bg-white border border-navy/10 rounded-sm p-5 space-y-3" data-testid="td-trustee-block">
          <h4 className="font-display text-lg text-navy">Trustee details</h4>
          <Field label="Full name" value={form.trustee_name} onChange={(v) => set("trustee_name", v)} testid="td-trustee-name" required />
          <Field label="Postal address (not published)" value={form.trustee_address} onChange={(v) => set("trustee_address", v)} testid="td-trustee-address" required multiline />
          <Field label="Email" value={form.trustee_email} onChange={(v) => set("trustee_email", v)} testid="td-trustee-email" type="email" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of birth (held by Commission only)" value={form.trustee_dob} onChange={(v) => set("trustee_dob", v)} testid="td-trustee-dob" type="date" required />
            <Select label="Role (optional)" value={form.trustee_role} onChange={(v) => set("trustee_role", v)} testid="td-trustee-role" options={TRUSTEE_ROLES} />
          </div>
          <Field label="Date signed" value={form.trustee_signed_on} onChange={(v) => set("trustee_signed_on", v)} testid="td-trustee-signed-on" type="date" required />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Signature</span>
              <button onClick={() => trusteeSig.current?.clear()} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
                <RotateCcw size={11} /> Clear
              </button>
            </div>
            <SignaturePad ref={trusteeSig} testid="td-trustee-sig" />
          </div>
        </section>

        {/* CHAIR BLOCK */}
        <section className="bg-white border border-navy/10 rounded-sm p-5 space-y-3" data-testid="td-chair-block">
          <h4 className="font-display text-lg text-navy">Counter-signed by Chair</h4>
          <Field label="Chair's full name" value={form.chair_name} onChange={(v) => set("chair_name", v)} testid="td-chair-name" required />
          <Field label="Date" value={form.chair_signed_on} onChange={(v) => set("chair_signed_on", v)} testid="td-chair-signed-on" type="date" required />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Signature</span>
              <button onClick={() => chairSig.current?.clear()} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
                <RotateCcw size={11} /> Clear
              </button>
            </div>
            <SignaturePad ref={chairSig} testid="td-chair-sig" />
          </div>
          <div className="pt-2 text-[11px] text-slate-500 leading-relaxed">
            <ShieldCheck size={11} className="inline mr-1 text-saffron" />
            By signing you confirm the trustee is willing to act and is not disqualified under the Charities Act 2011.
          </div>
        </section>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          onClick={() => post("trustee-declaration/preview", false)}
          disabled={busy !== null}
          data-testid="td-preview-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-pearl border border-navy/15 text-navy rounded-sm text-sm hover:bg-pearl-light disabled:opacity-50"
        >
          {busy === "trustee-declaration/preview" ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
          Preview PDF
        </button>
        <button
          onClick={() => post("trustee-declaration", true)}
          disabled={busy !== null}
          data-testid="td-download-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-sm text-sm font-medium hover:bg-saffron-light disabled:opacity-50"
        >
          {busy === "trustee-declaration" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Save &amp; download signed PDF
        </button>
        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
          <Lock size={11} /> Saved declarations are stored in <code className="text-saffron">db.trustee_declarations</code> and audit-logged.
        </span>
      </div>

      {/* History */}
      <section className="pt-6 mt-4 border-t border-navy/10" data-testid="td-history">
        <div className="flex items-center gap-3 mb-3">
          <h3 className="font-display text-lg text-navy">Past signed declarations</h3>
          <span className="text-xs text-slate-500">{history.length} on file</span>
        </div>
        {history.length === 0 ? (
          <p className="italic text-slate-500 text-sm">No declarations signed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-navy/10 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="py-2 pr-3">Trustee</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Signed on</th>
                  <th className="py-2 pr-3">Counter-signed by</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3 text-right">Download</th>
                </tr>
              </thead>
              <tbody>
                {history.map((d) => (
                  <tr key={d.doc_id} className="border-b border-navy/5" data-testid={`td-row-${d.doc_id}`}>
                    <td className="py-2 pr-3 text-navy">
                      {d.trustee_name}
                      <div className="text-[10px] text-slate-400">{d.trustee_email}</div>
                    </td>
                    <td className="py-2 pr-3 text-xs">{d.trustee_role || "—"}</td>
                    <td className="py-2 pr-3 text-xs">{d.trustee_signed_on}</td>
                    <td className="py-2 pr-3 text-xs">{d.chair_name}<div className="text-[10px] text-slate-400">{d.chair_signed_on}</div></td>
                    <td className="py-2 pr-3 text-xs">
                      {d.email_sent ? (
                        <span className="inline-flex items-center gap-1 text-green-700"><Mail size={11}/> Sent</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <a
                        href={`${API_BASE}/admin/governance/trustee-declaration/${d.doc_id}/pdf`}
                        target="_blank" rel="noreferrer"
                        data-testid={`td-dl-${d.doc_id}`}
                        className="inline-flex items-center gap-1 text-saffron text-xs underline"
                      >
                        <Download size={11} /> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, multiline }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {multiline ? (
        <textarea
          rows={3}
          value={value}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm"
        />
      ) : (
        <input
          type={type}
          value={value}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm"
        />
      )}
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}
